import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';
import { AuthLayout } from './layouts/AuthLayout';
import { useAuth } from './hooks/useAuth';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';

import AnimalsList from './pages/animals/AnimalsList';
import AnimalForm from './pages/animals/AnimalForm';
import AnimalProfile from './pages/animals/AnimalProfile';
import AnimalMovementPage from './pages/animals/AnimalMovementPage';
import AnimalLifecyclePage from './pages/animals/AnimalLifecyclePage';
import CattleRegisterPage from './pages/animals/CattleRegisterPage';

import QRScanPage from './pages/QRScanPage';

import FarmSetupPage from './pages/farm/FarmSetupPage';
import AssetsRegistryPage from './pages/assets/AssetsRegistryPage';

import DairyPage from './pages/dairy/DairyPage';
import BreedingPage from './pages/breeding/BreedingPage';
import HealthPage from './pages/health/HealthPage';
import PoultryPage from './pages/poultry/PoultryPage';
import CoconutPage from './pages/coconut/CoconutPage';
import InventoryPage from './pages/inventory/InventoryPage';

import ProcurementFinancePage from './pages/procurement/ProcurementFinancePage';
import TasksPage from './pages/operations/TasksPage';
import UtilitiesPage from './pages/utilities/UtilitiesPage';

import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import EnquiriesPage from './pages/EnquiriesPage';
import AdminPage from './pages/admin/AdminPage';

// Public marketing site (website/ folder merged in — this is now the main
// experience at "/"; the ILMS operational app lives behind Sign In at
// /dashboard and beyond). One scrolling page now (OnePage) instead of a
// route per section - the old per-section paths redirect to the matching
// in-page anchor so bookmarked/shared links still land correctly.
import PublicLayout from './public/components/Layout';
import PublicOnePage from './public/pages/OnePage';
import ShopLayout from './shop/ShopLayout';
import ShopHome from './shop/ShopHome';
import CartPage from './shop/CartPage';
import CheckoutPage from './shop/CheckoutPage';
import ShopOrdersPage from './shop/OrdersPage';
import OrderPage from './shop/OrderPage';
import AccountPage from './shop/AccountPage';
import SignupPage from './shop/SignupPage';
import OnlineShopPage from './pages/shop/OnlineShopPage';
import AgentPage from './agent/AgentPage';

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

export default function App() {
  return (
    <Routes>
      {/* Public marketing site — no login required */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<PublicOnePage />} />
        <Route path="/about" element={<Navigate to="/#about" replace />} />
        <Route path="/gallery" element={<Navigate to="/#gallery" replace />} />
        <Route path="/products" element={<Navigate to="/#products" replace />} />
        <Route path="/partners" element={<Navigate to="/#partners" replace />} />
        <Route path="/help" element={<Navigate to="/#help" replace />} />
        <Route path="/contact" element={<Navigate to="/#contact" replace />} />
      </Route>

      {/* Online shop - app-like, no login (pay by UPI, track by order number) */}
      <Route element={<ShopLayout />}>
        <Route path="/shop" element={<ShopHome />} />
        <Route path="/shop/cart" element={<CartPage />} />
        <Route path="/shop/checkout" element={<CheckoutPage />} />
        <Route path="/shop/orders" element={<ShopOrdersPage />} />
        <Route path="/shop/order/:ref" element={<OrderPage />} />
        <Route path="/shop/account" element={<AccountPage />} />
        <Route path="/shop/account/new" element={<SignupPage />} />
      </Route>

      {/* Delivery agents' page - a link to share, works in any phone browser (staff login) */}
      <Route path="/agent" element={<AgentPage />} />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
      </Route>

      {/* Internal ILMS app — behind Sign In */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        <Route path="/farm" element={<FarmSetupPage />} />
        <Route path="/assets" element={<AssetsRegistryPage />} />

        <Route path="/animals" element={<AnimalsList />} />
        <Route path="/animals/lifecycle" element={<AnimalLifecyclePage />} />
        <Route path="/cattle-register" element={<CattleRegisterPage />} />
        <Route path="/animals/movement" element={<AnimalMovementPage />} />
        <Route path="/animals/new" element={<AnimalForm />} />
        <Route path="/animals/:id" element={<AnimalProfile />} />
        <Route path="/animals/:id/edit" element={<AnimalForm />} />

        <Route path="/qr-scan" element={<QRScanPage />} />

        <Route path="/dairy" element={<DairyPage />} />
        <Route path="/breeding" element={<BreedingPage />} />
        <Route path="/health" element={<HealthPage />} />
        <Route path="/poultry" element={<PoultryPage />} />
        <Route path="/coconut" element={<CoconutPage />} />
        <Route path="/inventory" element={<InventoryPage />} />

        {/* Procurement & Finance was two separate pages (Procurement & Sales,
            Finance) with overlapping "purchase" concepts under different
            names -- merged into one; these two paths stay as redirects so
            old links/bookmarks and Dashboard's navigate('/finance') calls
            still land somewhere valid. */}
        <Route path="/procurement-finance" element={<ProcurementFinancePage />} />
        <Route path="/procurement-sales" element={<Navigate to="/procurement-finance" replace />} />
        <Route path="/finance" element={<Navigate to="/procurement-finance" replace />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/utilities" element={<UtilitiesPage />} />

        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/enquiries" element={<EnquiriesPage />} />
        <Route path="/online-shop" element={<OnlineShopPage />} />

        <Route path="/admin" element={<AdminPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
